/**
 * MW STUFFS - Sticky Mobile ATC v5
 *
 * v5 critical fix:
 * 1. Move element to document.body on connect (escapes Shopify section stacking contexts)
 *    Some Shopify themes wrap sections in containers with transform/filter/contain that
 *    create new stacking contexts, breaking position:fixed. Body-attach fixes this.
 * 2. IntersectionObserver with proper rootMargin
 * 3. Live updates from variant:change + cross-sell events
 */
(function () {
  if (customElements.get('sticky-atc')) return;

  function formatINR(paise) {
    const rupees = paise / 100;
    return '₹' + rupees.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function log(...args) {
    console.log('[MW Sticky]', ...args);
  }

  class StickyAtc extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.enableDesktop = this.dataset.enableDesktop === 'true';
      this.formId = this.dataset.sectionFormId;
      this.currentVariantPrice = null;
      this.crossSellActive = false;
      this.crossSellPrice = null;

      this.titleEl = this.querySelector('[data-sticky-title]');
      this.sizeEl = this.querySelector('[data-sticky-size]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.btnEl = this.querySelector('[data-sticky-btn]');
      this.btnTextEl = this.querySelector('[data-sticky-btn-text]');
    }

    connectedCallback() {
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;

      // === CRITICAL FIX: move element to body to escape stacking context issues ===
      // If the element is currently NOT a direct child of body, move it.
      // This ensures position:fixed works regardless of parent transform/filter/contain.
      if (this.parentElement !== document.body) {
        log('Moving sticky to <body> to escape stacking context');
        document.body.appendChild(this);
        // After move, re-query elements (they're still inside `this`, just re-confirm)
      }

      // Hide on desktop unless enabled
      if (!this.isMobile && !this.enableDesktop) {
        this.style.display = 'none';
        return;
      }

      this.attachObserver();
      this.attachListeners();

      // Capture initial variant price from DOM
      const priceText = this.priceEl?.textContent || '';
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        this.currentVariantPrice = Math.round(parseFloat(priceMatch[0].replace(/,/g, '')) * 100);
      }

      log('Initialized', {
        isMobile: this.isMobile,
        enableDesktop: this.enableDesktop,
        initialPrice: this.currentVariantPrice,
        parentTag: this.parentElement?.tagName,
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onVariantChange) document.removeEventListener('variant:change', this.onVariantChange);
    }

    attachObserver() {
      // Find the main ATC button - try multiple selectors in priority order
      const candidates = [
        document.querySelector('product-form .product-form__buttons'),
        document.querySelector('.product-form__buttons'),
        document.querySelector('[id^="ProductSubmitButton"]'),
        document.querySelector('product-form [name="add"]'),
        document.querySelector('button[name="add"]:not([form])'),
      ].filter(Boolean);

      // Filter out our own sticky button (which also has name="add")
      const mainBtn = candidates.find((el) => !this.contains(el));

      if (!mainBtn) {
        log('⚠️ No main ATC found - showing sticky on scroll-Y > 600px as fallback');
        this.attachScrollFallback();
        return;
      }

      log('Watching main ATC element:', mainBtn.tagName, mainBtn.className || mainBtn.id);

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const shouldShow = !entry.isIntersecting;
            this.classList.toggle('is-visible', shouldShow);
            log('Main ATC intersecting:', entry.isIntersecting, '→ sticky visible:', shouldShow);
          });
        },
        {
          threshold: 0,
          rootMargin: '0px 0px -10px 0px',
        }
      );
      this.observer.observe(mainBtn);
    }

    attachScrollFallback() {
      // Fallback: show after scrolling 600px down
      const checkScroll = () => {
        const shouldShow = window.scrollY > 600;
        this.classList.toggle('is-visible', shouldShow);
      };
      window.addEventListener('scroll', checkScroll, { passive: true });
      checkScroll();
    }

    attachListeners() {
      this.onVariantChange = this.handleVariantChange.bind(this);
      document.addEventListener('variant:change', this.onVariantChange);

      // Cross-sell checkbox toggle
      const crossCheckbox = document.querySelector('[data-bundle-crosssell]');
      if (crossCheckbox) {
        crossCheckbox.addEventListener('change', () => {
          this.crossSellActive = crossCheckbox.checked;
          this.captureCrossSellPrice();
          this.refreshDisplay();
          log('Cross-sell toggled:', this.crossSellActive);
        });
        // Initial state
        this.crossSellActive = crossCheckbox.checked;
        this.captureCrossSellPrice();
      }

      // Cross-sell pill clicks → may change variant price
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cross-pill]')) {
          setTimeout(() => {
            this.captureCrossSellPrice();
            if (this.crossSellActive) this.refreshDisplay();
          }, 50);
        }
      });

      // Window resize
      window.addEventListener('resize', () => {
        const wasMobile = this.isMobile;
        this.isMobile = window.matchMedia('(max-width: 749px)').matches;
        if (wasMobile !== this.isMobile) {
          if (!this.isMobile && !this.enableDesktop) {
            this.style.display = 'none';
            this.classList.remove('is-visible');
          } else {
            this.style.display = '';
          }
        }
      }, { passive: true });
    }

    captureCrossSellPrice() {
      const crossFinalEl = document.querySelector('[data-cross-final]');
      if (!crossFinalEl) return;
      const text = crossFinalEl.textContent || '';
      const match = text.match(/[\d,]+\.?\d*/);
      if (match) {
        this.crossSellPrice = Math.round(parseFloat(match[0].replace(/,/g, '')) * 100);
      }
    }

    handleVariantChange(e) {
      const variant = e.detail?.variant;
      if (!variant) return;

      this.currentVariantPrice = variant.price;

      let sizeText = variant.title;
      if (variant.options && variant.options.length > 0) {
        sizeText = variant.options.join(' / ');
      }
      if (this.sizeEl) this.sizeEl.textContent = sizeText;

      if (this.btnEl) {
        this.btnEl.disabled = !variant.available;
        if (this.btnTextEl) {
          this.btnTextEl.textContent = variant.available
            ? (this.btnEl.dataset.atcText || 'Add to Cart')
            : 'Sold Out';
        }
      }

      this.refreshDisplay();
      log('Variant changed:', { id: variant.id, price: variant.price, size: sizeText });
    }

    refreshDisplay() {
      if (!this.priceEl) return;

      let displayPrice = this.currentVariantPrice || 0;
      let buttonText = this.btnEl?.dataset.atcText || 'Add to Cart';

      if (this.crossSellActive && this.crossSellPrice) {
        displayPrice += this.crossSellPrice;
        buttonText = 'Add Both to Cart';
      }

      this.priceEl.textContent = formatINR(displayPrice);
      if (this.btnTextEl && this.btnEl && !this.btnEl.disabled) {
        this.btnTextEl.textContent = buttonText;
      }
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();